import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import type { MfaProviderType } from '@terra/shared-schemas-orchestrator';
import { applicationsClient } from '@terra/shared-rest-clients-applications';
import { useProjectStore } from '../store/projectStore';
import { getCurrentGatewayUrl } from '../utils/environmentDetection';
import { getAuthHeaders } from '../utils/authHeaders';

export type ApplicationCredentialMfaConfig = {
  id: string;
  providerType: MfaProviderType;
  isActive: boolean;
};

export type ApplicationCredential = {
  id: string;
  name?: string | null;
  role: string;
  username: string;
  loginUrl?: string;
  password?: string;
  basicAuthUsername?: string;
  basicAuthPassword?: string;
  mtlsCert?: string;
  mtlsKey?: string;
  mtlsPfx?: string;
  mtlsOrigin?: string;
  mtlsPassphrase?: string;
  headers: Array<{ key: string; value: string }>;
  cookies: Array<{ name: string; value: string }>;
  secrets: Array<{ key: string; value: string }>;
  mfaConfig: ApplicationCredentialMfaConfig[];
  generatedBy?: 'USER' | 'FEATURE_ANALYSIS' | null;
  isDisabled?: boolean;
  verification?: {
    status: 'PENDING' | 'VERIFYING' | 'VERIFIED' | 'FAILED';
    lastError: string | null;
    lastAttemptAt: Date | null;
    verifiedAt: Date | null;
  };
};

const EMPTY_ARRAY: ApplicationCredential[] = [];

type UseApplicationCredentialsOptions = {
  applicationId?: string;
  enabled?: boolean;
};

export function useApplicationCredentials({
  applicationId,
  enabled = true,
}: UseApplicationCredentialsOptions) {
  const selectedCompany = useProjectStore((state) => state.selectedCompany);
  const tenantId = selectedCompany?.tenantId;

  const query = useQuery({
    queryKey: ['browser-launcher-credentials', applicationId, tenantId],
    queryFn: async (): Promise<ApplicationCredential[]> => {
      if (!applicationId || !tenantId) {
        return [];
      }

      const authHeaders = await getAuthHeaders();
      const response = await applicationsClient.getCredentials({
        params: { id: applicationId },
        query: { decrypt: true, includeDisabled: true },
        overrideClientOptions: {
          baseUrl: `${getCurrentGatewayUrl()}/orchestrator`,
        },
        extraHeaders: authHeaders,
      });

      if (response.status !== 200) {
        throw new Error('Failed to fetch credentials');
      }

      const credentialsArray = response.body.credentials;

      if (!Array.isArray(credentialsArray)) {
        console.error(
          '[useApplicationCredentials] Unexpected response format:',
          response.body,
        );

        return [];
      }

      return credentialsArray.map((cred) => ({
        id: cred.id,
        name: cred.name ?? null,
        role: cred.role,
        username: cred.username,
        loginUrl: cred.loginUrl ?? undefined,
        password: cred.password ?? undefined,
        basicAuthUsername: cred.basicAuthUsername ?? undefined,
        basicAuthPassword: cred.basicAuthPassword ?? undefined,
        mtlsCert: cred.mtlsCert ?? undefined,
        mtlsKey: cred.mtlsKey ?? undefined,
        mtlsPfx: cred.mtlsPfx ?? undefined,
        mtlsOrigin: cred.mtlsOrigin ?? undefined,
        mtlsPassphrase: cred.mtlsPassphrase ?? undefined,
        headers: cred.headers,
        cookies: cred.cookies.map((c) => ({ name: c.name, value: c.value })),
        secrets: cred.secrets,
        mfaConfig: (cred.mfaConfig ?? []).flatMap((cfg) => {
          if (!cfg.providerType) {
            return [];
          }

          return [
            {
              id: cfg.id,
              providerType: cfg.providerType,
              isActive: cfg.isActive,
            },
          ];
        }),
        generatedBy: cred.generatedBy,
        isDisabled: cred.isDisabled,
        verification: cred.verification ?? undefined,
      }));
    },
    enabled: enabled && !!applicationId && !!tenantId,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

  const credentials = useMemo(() => {
    const data = query.data;

    if (Array.isArray(data)) {
      return data;
    }

    return EMPTY_ARRAY;
  }, [query.data]);

  return {
    credentials,
    isLoading: query.isLoading,
    error: query.error,
    refetch: query.refetch,
  };
}
